import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ServiceCategories = ({ activeCategory = null }) => {
  const [internalActiveCategory, setInternalActiveCategory] = useState('launch-blueprint');

  useEffect(() => {
    const applyHash = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash && categories.some(cat => cat.id === hash)) {
        setInternalActiveCategory(hash);
      }
    };

    applyHash();
    window.addEventListener('hashchange', applyHash);
    return () => window.removeEventListener('hashchange', applyHash);
  }, []);
  
  // Use activeCategory prop if provided, otherwise use internal state
  const currentActiveCategory = activeCategory || internalActiveCategory;

  const categories = [
    {
      id: 'launch-blueprint',
      title: 'Launch Blueprint', 
      icon: 'Flag',
      description: 'Six milestone launch program with executive governance',
      color: 'bg-blue-600',
      services: [
        {
          name: 'Phase 0 | Board Alignment',
          description: 'Executive workshops, business case validation, risk registers.',
          impact: 'CXO alignment in 10 days, board-ready deck delivered.',
          features: [
            'Stakeholder alignment sessions',
            'Board decision toolkit', 
            'Program risk register',
            'Launch governance charter'
          ],
          icon: 'Users'
        },
        {
          name: 'Phase 1 | Entity & Infrastructure',
          description: 'Entity-in-a-Box™ delivery, banking, workspace, zero-trust stack.',
          impact: 'Legal + IT compliant within 6 weeks.',
          features: [
            'Entity formation & banking',
            'Regulatory registrations',
            'Zero-trust network design',
            'Hybrid workspace specification'
          ],
          icon: 'Target'
        },
        {
          name: 'Phase 2 | Talent & Operations',
          description: 'Leadership hires, Talent Cloud 100™, SLA-based managed operations.',
          impact: 'First 5 leaders + 100 screened CVs in 8 weeks.',
          features: [
            'Leadership search delivery',
            'Talent Cloud 100™ pipeline',
            'Managed GCC Ops™ onboarding',
            'Executive cockpit readiness'
          ],
          icon: 'Brain'
        }
      ]
    },
    {
      id: 'pricing',
      title: 'Transparent Pricing',
      icon: 'Receipt',
      description: 'Open rate card with pass-through government costs',
      color: 'bg-blue-500',
      services: [
        {
          name: 'LaunchPad® Feasibility Sprint',
          description: 'Board-ready "Go / No-Go" deck covering talent, cost and risk',
          timeline: '4 weeks',
          features: ['Market viability assessment', 'Cost-benefit analysis', 'Risk evaluation framework', 'Executive decision support'],
          pricing: 'Fixed rate - published pricing on request'
        },
        {
          name: 'Entity-in-a-Box™',
          description: 'Day-1-compliant Indian subsidiary—legal entity, banking, directors',
          timeline: '6 weeks',
          features: ['Legal entity formation', 'Banking setup', 'Director appointments', 'Compliance framework'],
          pricing: 'Fixed rate - no hidden complexity mark-ups'
        },
        {
          name: 'Talent Cloud 100™',
          description: 'First 5 leadership hires + 100 screened CVs',
          timeline: '< 8 weeks',
          features: ['Leadership recruitment', 'CV screening process', 'Talent pool development', 'Hiring strategy'],
          pricing: 'Performance-based pricing available'
        },
        {
          name: 'Digital Workplace FastTrack™',
          description: 'Secure IT, Zero-Trust network & hybrid-ready office spec',
          timeline: '6 weeks',
          features: ['IT infrastructure setup', 'Zero-Trust security', 'Hybrid office design', 'Technology integration'],
          pricing: 'Transparent cost structure'
        },
        {
          name: 'Managed GCC Ops™',
          description: 'HR, finance, admin & facilities under SLA so you stay strategic',
          timeline: 'Monthly subscription',
          features: ['HR operations', 'Financial management', 'Administrative support', 'Facility management'],
          pricing: 'SLA-based monthly subscription'
        }
      ]
    },
    {
      id: 'risk',
      title: 'Zero-Risk Engagement',
      icon: 'Shield',
      description: 'Walk-away options and risk-mitigation baked into contracts',
      color: 'bg-purple-500',
      services: [
        {
          name: 'Exit in 90 Days',
          description: 'Walk away in 3-6 months with zero termination penalties.',
          bestWhen: 'You need proof before scaling headcount.',
          features: ['3-6 month exit clause', 'Transferable vendor contracts', 'IP & data residency controls', 'Transition playbook ready'],
          snapshot: 'Scale with confidence, disengage without friction'
        },
        {
          name: 'Governance Guardrails',
          description: 'Audit-ready controls, Big-4 benchmarked governance and real-time cockpit.',
          bestWhen: 'Regulated industries and security-first mandates.',
          features: ['Quarterly risk audits', 'Steady-state handover criteria', 'Joint governance council', 'Continuous compliance monitoring'],
          snapshot: 'Controls you can demonstrate on Day One'
        },
        {
          name: 'Performance Safeguards',
          description: 'Launch SLAs, refund triggers, success-based fees for critical milestones.',
          bestWhen: 'You need commercially-backed assurance.',
          features: ['SLA-backed milestones', 'Refund triggers', 'Success-based incentives', 'Dedicated escalation council'],
          snapshot: 'Commercially aligned, zero-surprise economics'
        }
      ]
    },
    {
      id: 'intelligence',
      title: 'AI Intelligence Layer',
      icon: 'Brain',
      description: 'Bootminds GCC Insight Platform - included free for the first year',
      color: 'bg-green-500',
      services: [
        {
          name: 'Real-time CXO Dashboards',
          description: 'Comprehensive executive visibility into all GCC operations',
          features: ['Headcount tracking', 'Cost monitoring', 'Productivity metrics', 'Compliance status'],
          benefits: 'Instant visibility into key metrics'
        },
        {
          name: 'Predictive Alerts',
          description: 'AI-powered early warning system for operational risks',
          features: ['Hiring lag predictions', 'Attrition risk alerts', 'Cash-flow slippage warnings', 'Performance anomalies'],
          benefits: 'Proactive issue prevention'
        },
        {
          name: '"What-If" Scenarios',
          description: 'Model tax, incentive and capacity moves in seconds',
          features: ['Tax scenario modeling', 'Incentive impact analysis', 'Capacity planning', 'Financial projections'],
          benefits: 'Data-driven decision making'
        }
      ]
    },
    {
      id: 'engagement-models',
      title: 'Engagement Models',
      icon: 'Handshake',
      description: 'Find your perfect GCC partnership approach',
      color: 'bg-purple-600',
      services: [
        {
          name: 'Interactive Model Finder',
          description: 'Answer a few questions to discover your ideal engagement approach',
          impact: 'Get personalized recommendations in 2 minutes',
          features: [
            'Quick assessment questionnaire',
            'Custom model recommendations',
            'Detailed partnership options',
            'Expert-backed insights'
          ],
          icon: 'Target',
          benefits: 'Find your perfect match'
        },
        {
          name: 'Partnership Options',
          description: 'Explore our strategic engagement models for GCC success',
          impact: 'Flexible approaches for every organization',
          features: [
            'Managed Services Model',
            'Strategic Partnership',
            'Talent-as-a-Service',
            'Build-Operate-Transfer'
          ],
          icon: 'Users',
          benefits: 'Tailored to your needs'
        }
      ]
    }
  ];

  const activeData = categories.find(cat => cat.id === currentActiveCategory);

  const renderSolutionStackCard = (service, index) => (
    <div key={index} className="card-interactive rounded-xl p-8">
      <div className="mb-6">
        <div className="w-16 h-16 bg-primary rounded-xl flex items-center justify-center mb-4">
          <Icon name={service.icon} size={32} color="white" />
        </div>
        <h4 className="text-2xl font-headline font-semibold text-text-primary mb-3">
          {service.name}
        </h4>
        <p className="text-text-secondary mb-4 leading-relaxed">
          {service.description}
        </p>
      </div>

      <div className="mb-6">
        <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Icon name="TrendingUp" size={20} color="var(--color-accent)" className="mt-0.5 flex-shrink-0" />
            <div>
              <span className="text-xs font-medium text-accent uppercase tracking-wide">Performance Impact</span>
              <p className="text-sm text-text-primary font-semibold mt-1">{service.impact}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-6">
        <h5 className="font-medium text-text-primary mb-3">What We Do:</h5>
        <ul className="space-y-2">
          {service.features.map((feature, idx) => (
            <li key={idx} className="flex items-start space-x-2 text-sm text-text-secondary">
              <Icon name="Check" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

  const renderProductCard = (service, index) => (
    <div key={index} className="card-interactive rounded-xl p-6">
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xl font-headline font-semibold text-text-primary">
            {service.name}
          </h4>
          {service.timeline && (
            <span className="bg-accent/10 text-accent px-3 py-1 rounded-full text-sm font-medium">
              {service.timeline}
            </span>
          )}
        </div>
        <p className="text-text-secondary text-sm mb-4">
          {service.description}
        </p>
      </div>

      <div className="mb-6">
        <h5 className="font-medium text-text-primary mb-2">Key Deliverables:</h5>
        <ul className="space-y-1">
          {service.features.map((feature, idx) => (
            <li key={idx} className="flex items-start space-x-2 text-sm text-text-secondary">
              <Icon name="Check" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-accent font-semibold text-sm">
          {service.pricing}
        </div>
        <Button
          variant="outline"
          size="sm"
          iconName="Calendar"
          iconPosition="right"
        >
          Schedule Consultation
        </Button>
      </div>
    </div>
  );

  const renderEngagementCard = (service, index) => (
    <div key={index} className="card-interactive rounded-xl p-6">
      <div className="mb-4">
        <h4 className="text-xl font-headline font-semibold text-text-primary mb-2">
          {service.name}
        </h4>
        <div className="bg-muted p-3 rounded-lg mb-3">
          <span className="text-xs font-medium text-text-secondary uppercase tracking-wide">Best When You Want</span>
          <p className="text-sm text-text-primary font-medium">{service.bestWhen}</p>
        </div>
        <p className="text-text-secondary text-sm mb-4">
          {service.description}
        </p>
      </div>

      <div className="mb-6">
        <h5 className="font-medium text-text-primary mb-2">Key Characteristics:</h5>
        <ul className="space-y-1">
          {service.features.map((feature, idx) => (
            <li key={idx} className="flex items-start space-x-2 text-sm text-text-secondary">
              <Icon name="Check" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="bg-blue-50 p-3 rounded-lg mb-4">
        <span className="text-xs font-medium text-blue-600 uppercase tracking-wide">Snapshot</span>
        <p className="text-sm text-blue-800 font-medium">{service.snapshot}</p>
      </div>
    </div>
  );

  const renderIntelligenceCard = (service, index) => (
    <div key={index} className="card-interactive rounded-xl p-6">
      <div className="mb-4">
        <h4 className="text-xl font-headline font-semibold text-text-primary mb-2">
          {service.name}
        </h4>
        <p className="text-text-secondary text-sm mb-4">
          {service.description}
        </p>
      </div>

      <div className="mb-6">
        <h5 className="font-medium text-text-primary mb-2">Features:</h5>
        <ul className="space-y-1">
          {service.features.map((feature, idx) => (
            <li key={idx} className="flex items-start space-x-2 text-sm text-text-secondary">
              <Icon name="Zap" size={16} color="var(--color-accent)" className="mt-0.5 flex-shrink-0" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="bg-green-50 p-3 rounded-lg mb-4">
        <span className="text-xs font-medium text-green-600 uppercase tracking-wide">Benefit</span>
        <p className="text-sm text-green-800 font-medium">{service.benefits}</p>
      </div>
    </div>
  );

  return (
    <div className="bg-background py-8">
      <div className="container-executive">
        {/* Show category selector only if no active category is provided */}
        {!activeCategory && (
          <>
            {/* Our Point of View Section */}
            <div className="text-center mb-16">
              <div className="bg-card border border-border rounded-2xl p-8 lg:p-12 mb-12">
                <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-body-medium mb-6">
                  <Icon name="Lightbulb" size={16} />
                  <span>Our Point of View</span>
                </div>
                
                <h2 className="text-2xl lg:text-3xl font-headline font-bold text-text-primary mb-4">
                  "Beyond Cost & Scale"
                </h2>
                
                <p className="text-lg text-text-secondary mb-6 max-w-3xl mx-auto">
                  Our 2025 survey shows the winners focus on nine enablers. The three with the 
                  <strong> biggest performance delta</strong> are:
                </p>

                <div className="grid md:grid-cols-3 gap-6 mb-6">
                  <div className="text-center p-4 bg-muted rounded-lg">
                    <div className="text-2xl font-bold text-text-primary mb-2">1</div>
                    <h4 className="font-semibold text-text-primary">Modular algorithms</h4>
                  </div>
                  
                  <div className="text-center p-4 bg-muted rounded-lg">
                    <div className="text-2xl font-bold text-text-primary mb-2">2</div>
                    <h4 className="font-semibold text-text-primary">Leadership empowerment & decision rights</h4>
                  </div>
                  
                  <div className="text-center p-4 bg-muted rounded-lg">
                    <div className="text-2xl font-bold text-text-primary mb-2">3</div>
                    <h4 className="font-semibold text-text-primary">Micro-services / API-first tech</h4>
                  </div>
                </div>

                <p className="text-text-primary font-semibold text-lg">
                  We bake these into every Bootminds engagement.
                </p>
              </div>
            </div>

            <div className="text-center mb-12" id="launch-blueprint">
              <h2 className="text-3xl lg:text-4xl font-headline font-bold text-text-primary mb-4">
                Bootminds | AI-Powered GCC Solutions for India
              </h2>
              <p className="text-lg text-text-secondary max-w-4xl mx-auto mb-6">
                Turn bold ambitions into a high-performance India GCC—supported by real-time AI insights, 
                fixed milestones, and a transparent rate card.
              </p>
            </div>

            {/* Category Navigation */}
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setInternalActiveCategory(category.id)}
                  className={`flex items-center space-x-3 px-6 py-3 rounded-lg transition-all duration-300 ${
                    currentActiveCategory === category.id
                      ? 'bg-primary text-primary-foreground shadow-executive'
                      : 'bg-muted text-text-primary hover:bg-primary/10'
                  }`}
                >
                  <div className={`w-8 h-8 ${category.color} rounded-lg flex items-center justify-center`}>
                    <Icon name={category.icon} size={18} color="white" />
                  </div>
                  <span className="font-medium">{category.title}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {/* Anchor markers for hash navigation */}
        <div className="hidden">
          {categories.map(category => (
            <span key={category.id} id={category.id}></span>
          ))}
        </div>

        {/* Active Category Content */}
        {activeData && (
          <>
            <div className="mb-8">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-headline font-semibold text-text-primary mb-2">
                  {activeData.title}
                </h3>
                <p className="text-text-secondary">{activeData.description}</p>
              </div>

              <div className={`grid gap-6 ${
                currentActiveCategory === 'solution-stack' ? 'grid-cols-1 lg:grid-cols-3' :
                currentActiveCategory === 'products' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' :
                currentActiveCategory === 'engagement-models'? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
              }`}>
                {activeData.services.map((service, index) => {
                  if (currentActiveCategory === 'solution-stack') {
                    return renderSolutionStackCard(service, index);
                  } else if (currentActiveCategory === 'products') {
                    return renderProductCard(service, index);
                  } else if (currentActiveCategory === 'engagement-models') {
                    return renderEngagementCard(service, index);
                  } else {
                    return renderIntelligenceCard(service, index);
                  }
                })}
              </div>
            </div>

            {/* Why CXOs Choose Bootminds - only show if no specific category */}
            {!activeCategory && (
              <div className="bg-muted rounded-xl p-8">
                <h3 className="text-xl font-headline font-semibold text-text-primary mb-6 text-center">
                  Why CXOs Choose Bootminds
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-blue-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <Icon name="Zap" size={24} color="white" />
                    </div>
                    <h4 className="font-medium text-text-primary mb-2">Speed with Certainty</h4>
                    <p className="text-sm text-text-secondary">
                      India-ready in 12-20 weeks, governed by milestone SLAs
                    </p>
                  </div>

                  <div className="text-center">
                    <div className="w-16 h-16 bg-green-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <Icon name="DollarSign" size={24} color="white" />
                    </div>
                    <h4 className="font-medium text-text-primary mb-2">Transparent Economics</h4>
                    <p className="text-sm text-text-secondary">
                      Open rate card, fixed fees, pass-through govt. costs
                    </p>
                  </div>

                  <div className="text-center">
                    <div className="w-16 h-16 bg-purple-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <Icon name="Shield" size={24} color="white" />
                    </div>
                    <h4 className="font-medium text-text-primary mb-2">Big-4-grade Controls</h4>
                    <p className="text-sm text-text-secondary">
                      KPMG-benchmarked governance from Day 1
                    </p>
                  </div>

                  <div className="text-center">
                    <div className="w-16 h-16 bg-orange-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <Icon name="Brain" size={24} color="white" />
                    </div>
                    <h4 className="font-medium text-text-primary mb-2">AI-Driven Decisions</h4>
                    <p className="text-sm text-text-secondary">
                      Every metric surfaced in a single executive cockpit
                    </p>
                  </div>
                </div>

                <div className="text-center mt-8">
                  <Button
                    variant="default"
                    size="lg"
                    iconName="Calendar"
                    iconPosition="left"
                    className="bg-accent hover:bg-accent/90"
                  >
                    Explore Service Details
                  </Button>
                  <p className="text-sm text-text-secondary mt-2">
                    Leave with a customised timeline, cost forecast, and access to our AI sandbox
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ServiceCategories;